import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { Alert, Col, Row, Table } from 'react-bootstrap'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faFileCertificate,
  faInfoCircle,
  faSearch,
} from '@fortawesome/pro-solid-svg-icons'

import { IMainState } from 'store/states'
import Loader from 'components/ui/loader/Loader'
import { ICertificatesState } from 'store/states/certificates.state'
import TableHeader from 'components/ui/table-header/TableHeader'
import { useWindowSize } from 'hooks/react.hooks'
import { mobileWidth } from 'enum/responsive-width.enum'
import * as fromActions from 'store/actions/certificates.action'
import { Actions as ExternalCourseCertificateActions } from 'store/actions/externalCourseCertificate.action'
import { ContentContainer } from 'components/ui/content-container/ContentContainer'
import styles from './MyCertificates.module.scss'
import { CertificatesTypes, prefix } from './constants'
import { Certificate, External, Internal } from './types'
import { TableRow } from './TableRow'

interface CertificatesProps {
  title?: string
}

const MyCertificates: React.FC<CertificatesProps> = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const [onDesktop, setOnDesktop] = useState(true)
  const windowsSize = useWindowSize()

  useEffect(() => {
    if (windowsSize.width)
      setOnDesktop(windowsSize.width < mobileWidth ? false : true)
  }, [windowsSize])

  const { loading: loadingCertificate, myCertificates } = useSelector(
    ({ certificate }: IMainState) => certificate as ICertificatesState
  )
  const { loading: loadingBase, list } = useSelector(
    ({ externalCourseCertificate }: IMainState) => externalCourseCertificate
  )

  const loading = loadingCertificate || loadingBase

  const certificates = React.useMemo(() => {
    const newList = list.map<External>((certificate) => ({
      ...certificate,
      type: CertificatesTypes.external,
    }))
    const newMyCertificates = myCertificates.map<Internal>((certificate) => ({
      ...certificate,
      type: CertificatesTypes.internal,
    }))
    const array: Certificate[] = [...newList, ...newMyCertificates]
    return array.sort((a, b) => a.name.localeCompare(b.name))
  }, [myCertificates, list])

  const headersData = [
    { label: t(`${prefix}.table.name`) },
    { label: t(`${prefix}.table.type`) },
    { label: t(`${prefix}.table.source`) },
    { label: t(`${prefix}.table.createdAt`) },
    { label: '' },
  ]

  useEffect(() => {
    dispatch({ type: fromActions.GET_MY_CERTIFICATES })
    dispatch(ExternalCourseCertificateActions.getList())
  }, [dispatch])

  return loading ? (
    <Loader overlay color />
  ) : (
    <ContentContainer
      title={t('home.lmsIndex.lmsCertificates.lmsCertificatesTitle')}
    >
      <Row className="d-flex align-items-center">
        {onDesktop && (
          <Col>
            <div className={styles.lmsCertificatesAvaliable}>
              <FontAwesomeIcon icon={faFileCertificate} />
              {t('home.lmsIndex.lmsCertificates.lmsCertificatesAvaliable', {
                count: certificates.length,
              })}
            </div>
          </Col>
        )}
        <Col className={`${!onDesktop && 'pr-0'}`}>
          <div className={styles.search}>
            <input
              type="text"
              placeholder={t('administration.certificate.list.search')}
            />
            <FontAwesomeIcon icon={faSearch} className="icon" />
          </div>
        </Col>
      </Row>
      {!onDesktop && (
        <Row className="mb-3">
          <Col>
            <div className={styles.lmsCertificatesAvaliable}>
              <FontAwesomeIcon icon={faFileCertificate} />
              {t('home.lmsIndex.lmsCertificates.lmsCertificatesAvaliable', {
                count: certificates.length,
              })}
            </div>
          </Col>
        </Row>
      )}
      <Row>
        <Col className="pr-0">
          {certificates.length ? (
            <div className={styles.tableContainer}>
              <Table hover responsive className={styles.table}>
                <thead className={styles.tableHeader}>
                  <TableHeader
                    headers={
                      onDesktop ? headersData : [headersData[0], headersData[3]]
                    }
                  />
                </thead>
                <tbody className={styles.tableBody}>
                  {certificates.map((row) => {
                    const internal =
                      row.type === CertificatesTypes.internal ? row : undefined
                    const id = internal?.certificate?.id || row.id
                    const courseId = internal?.courseId
                    const key = [id, courseId].join('-')

                    return (
                      <TableRow key={key} onDesktop={onDesktop} row={row} />
                    )
                  })}
                </tbody>
              </Table>
            </div>
          ) : (
            <Alert variant="secondary" className={styles.notLoaded}>
              <FontAwesomeIcon icon={faInfoCircle}></FontAwesomeIcon>
              <span className={styles.notLoadedText}>
                {t(`${prefix}.noAvailableCertificates`)}
              </span>
            </Alert>
          )}
        </Col>
      </Row>
    </ContentContainer>
  )
}

export default MyCertificates
